import React, { useState, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { colors, typography, spacing } from '../../theme';
import { useAsyncAnalysis } from '../../hooks/useAsyncAnalysis';
import { validateText } from '../../utils/text-validation';
import { validateUrl } from '../../utils/url-validation';
import { validateFile } from '../../utils/file-validation';
import { TechniqueDefinitionsService, type TechniqueDefinition } from '../../services/technique-definitions.service';
import type { AnalysisSession, TechniquesResult } from '../../types/analysis-session';
import { InputPreview } from '../PipelineAnalysis/sections/InputPreview';
import { TechniquesResults } from '../PipelineAnalysis/sections/TechniquesResults';
import { VerdictSection } from '../PipelineAnalysis/styles';

// =============================================================================
// Input Types
// =============================================================================

type InputType = 'text' | 'image' | 'audio' | 'video' | 'url';

const INPUT_TYPES: { key: InputType; labelKey: string; enabled: boolean; accept?: string }[] = [
  { key: 'text', labelKey: 'common.text', enabled: true },
  { key: 'image', labelKey: 'common.image', enabled: true, accept: 'image/jpeg,image/png,image/webp,image/gif' },
  { key: 'audio', labelKey: 'common.audio', enabled: true, accept: 'audio/mpeg,audio/wav,audio/ogg,audio/mp4' },
  { key: 'video', labelKey: 'common.video', enabled: true, accept: 'video/mp4,video/webm,video/ogg' },
  { key: 'url', labelKey: 'common.url', enabled: true },
];

// =============================================================================
// Component
// =============================================================================

export const ManipulationTechniques: React.FC = () => {
  const { t } = useTranslation();
  const async = useAsyncAnalysis('techniques');
  const [inputType, setInputType] = useState<InputType>('text');
  const [inputText, setInputText] = useState('');
  const [inputUrl, setInputUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedResult, setParsedResult] = useState<TechniquesResult | null>(null);
  const [analyzedInput, setAnalyzedInput] = useState<{ type: InputType; text: string } | null>(null);
  const [techniqueDefinitions, setTechniqueDefinitions] = useState<TechniqueDefinition[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Load technique definitions once (cached in service)
  useEffect(() => {
    TechniqueDefinitionsService.getDefinitions()
      .then(setTechniqueDefinitions)
      .catch(err => console.warn('Failed to load technique definitions:', err));
  }, []);

  // Parse async result (AnalysisSession) into TechniquesResult when it arrives
  useEffect(() => {
    if (!async.result) return;
    try {
      const session: AnalysisSession = async.result;
      if (session.techniques) {
        setParsedResult(session.techniques);
      }
      // For media inputs, swap snapshot with the extracted text once available
      if (session.input_text && session.input_type !== 'text' && session.input_type !== 'url') {
        setAnalyzedInput(prev => prev ? { ...prev, text: session.input_text! } : prev);
      }
    } catch {
      // result parsing failed — error state handled by hook
    }
  }, [async.result]);

  const [fileError, setFileError] = useState<string | null>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateFile(file, inputType);
    if (!validation.valid) {
      setFileError(validation.error);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setFileError(null);
    setSelectedFile(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleTypeChange = (type: InputType) => {
    setInputType(type);
    setSelectedFile(null);
    setParsedResult(null);
    setAnalyzedInput(null);
    async.reset();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const textValidation = useMemo(() => validateText(inputText), [inputText]);
  const urlValidation = useMemo(() => validateUrl(inputUrl), [inputUrl]);

  const canAnalyze =
    inputType === 'text' ? textValidation.valid :
    inputType === 'url' ? urlValidation.valid :
    selectedFile !== null;

  const handleAnalyze = () => {
    if (!canAnalyze) return;
    setParsedResult(null);

    if (inputType === 'text') {
      const txt = inputText.trim();
      setAnalyzedInput({ type: inputType, text: txt });
      async.submitText(txt);
    } else if (inputType === 'url') {
      const url = inputUrl.trim();
      setAnalyzedInput({ type: inputType, text: url });
      async.submitUrl(url);
    } else if (selectedFile) {
      setAnalyzedInput({ type: inputType, text: selectedFile.name });
      async.submitMedia(selectedFile, inputType);
    }
  };

  // Aliases for template compatibility
  const isAnalyzing = async.isAnalyzing;
  const error = async.error;
  const result = parsedResult;
  const { skipped, skipMessage } = async;

  return (
    <Container>
      {/* Header */}
      <Header>
        <Title>{t('techniques.title')}</Title>
        <Subtitle>{t('techniques.subtitle')}</Subtitle>
      </Header>

      {/* Input Type Selector */}
      <TypeSelector>
        {INPUT_TYPES.map(({ key, labelKey, enabled }) => (
          <TypeButton
            key={key}
            active={inputType === key}
            disabled={!enabled}
            onClick={() => enabled && handleTypeChange(key)}
          >
            {t(labelKey)}
          </TypeButton>
        ))}
      </TypeSelector>

      {/* Input Area */}
      <InputArea>
        {inputType === 'text' ? (
          <TextInput
            placeholder={t('techniques.textPlaceholder')}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            disabled={isAnalyzing}
          />
        ) : inputType === 'url' ? (
          <UrlInputWrapper>
            <UrlInput
              type="url"
              placeholder={t('techniques.urlPlaceholder')}
              value={inputUrl}
              onChange={(e) => setInputUrl(e.target.value)}
              disabled={isAnalyzing}
            />
          </UrlInputWrapper>
        ) : (
          <FileDropZone>
            <input
              ref={fileInputRef}
              type="file"
              accept={INPUT_TYPES.find(t => t.key === inputType)?.accept}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            {selectedFile ? (
              <FileSelected>
                <FileName>{selectedFile.name}</FileName>
                <FileSize>{(selectedFile.size / (1024 * 1024)).toFixed(1)} MB</FileSize>
                <RemoveFileBtn onClick={handleRemoveFile}>Remove</RemoveFileBtn>
              </FileSelected>
            ) : (
              <DropPlaceholder onClick={() => fileInputRef.current?.click()}>
                <DropLabel>Click to select {inputType} file</DropLabel>
                <DropHint>
                  {inputType === 'image' && t('pipeline.imageHint')}
                  {inputType === 'audio' && t('pipeline.audioHint')}
                  {inputType === 'video' && t('pipeline.videoHint')}
                </DropHint>
              </DropPlaceholder>
            )}
            {fileError && <FileErrorMsg>{fileError}</FileErrorMsg>}
          </FileDropZone>
        )}

        <InputFooter>
          <CharCount style={
            inputType === 'text'
              ? { color: textValidation.level === 'error' ? '#ef4444' : textValidation.level === 'warning' ? '#eab308' : '#22c55e' }
              : inputType === 'url' && urlValidation.error
                ? { color: '#ef4444' }
                : inputType === 'url' && urlValidation.valid
                  ? { color: '#22c55e' }
                  : undefined
          }>
            {inputType === 'text'
              ? (textValidation.error || textValidation.warning || `${inputText.trim().length} characters`)
              : inputType === 'url'
                ? (urlValidation.error || (inputUrl.trim() ? t('common.validUrl') : t('common.enterUrl')))
                : selectedFile ? t('common.readyToAnalyze') : t('common.noFileSelected')}
          </CharCount>
          <AnalyzeBtn
            onClick={handleAnalyze}
            disabled={isAnalyzing || !canAnalyze}
          >
            {isAnalyzing ? (
              <>
                <Spinner />
                {async.statusText || t('common.analyzing')}
              </>
            ) : (
              t('common.analyze')
            )}
          </AnalyzeBtn>
        </InputFooter>
      </InputArea>

      {/* Error */}
      {error && (
        <ErrorBox>
          <ErrorIcon>!</ErrorIcon>
          {error}
        </ErrorBox>
      )}

      {/* Skipped */}
      {skipped && skipMessage && (
        <SkipBox>
          <SkipText>{skipMessage}</SkipText>
          <SkipAction onClick={() => { async.reset(); handleTypeChange('image'); }}>
            {t('common.uploadManual')}
          </SkipAction>
        </SkipBox>
      )}

      {/* Backend Warnings */}
      {async.warnings.length > 0 && (
        <WarningBox>
          {async.warnings.map((w, i) => <div key={i}>{w}</div>)}
        </WarningBox>
      )}

      {/* Input Preview (shown once result lands) */}
      {result && analyzedInput && (
        <InputPreview inputType={analyzedInput.type} text={analyzedInput.text} />
      )}

      {/* Results — editorial layout */}
      {result && (
        <VerdictSection>
          <TechniquesResults result={result} techDefs={techniqueDefinitions} />
        </VerdictSection>
      )}
    </Container>
  );
};

// =============================================================================
// Styled Components — input form & banners (results use shared editorial components)
// =============================================================================

const Container = styled.div`
  width: 100%;
  max-width: 1800px;
  margin: 0 auto;
  padding: 0;
  @media (max-width: 768px) { padding: 0; }
`;

const Header = styled.div`
  margin-bottom: ${spacing.xl}px;
`;

const Title = styled.h1`
  font-family: ${typography.fontFamily.primary};
  font-size: 28px;
  font-weight: ${typography.fontWeight.bold};
  color: #ffffff;
  margin: 0 0 ${spacing.xs}px 0;

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};
  }
`;

const Subtitle = styled.p`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  color: rgba(255, 255, 255, 0.5);
  margin: 0;

  [data-theme="light"] & {
    color: ${colors.steelGray};
  }
`;

/* Input Type Selector */
const TypeSelector = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  margin-bottom: ${spacing.lg}px;
  border: 1px solid rgba(255, 255, 255, 0.06);

  [data-theme="light"] & {
    background: ${colors.warmGray};
    border-color: ${colors.lightGray};
  }
`;

const TypeButton = styled.button<{ active?: boolean; disabled?: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${props => props.active ? typography.fontWeight.semibold : typography.fontWeight.medium};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  position: relative;

  background: ${props => props.active
    ? 'rgba(139, 92, 246, 0.2)'
    : 'transparent'};
  color: ${props => props.active
    ? '#a78bfa'
    : props.disabled
      ? 'rgba(255, 255, 255, 0.2)'
      : 'rgba(255, 255, 255, 0.6)'};

  ${props => props.active && `
    box-shadow: 0 0 0 1px rgba(139, 92, 246, 0.3);
  `}

  [data-theme="light"] & {
    background: ${props => props.active ? 'white' : 'transparent'};
    color: ${props => props.active
      ? colors.neonViolet
      : props.disabled
        ? colors.hintGray
        : colors.steelGray};
    ${props => props.active && `
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    `}
  }

  &:hover:not(:disabled) {
    background: ${props => props.active ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255, 255, 255, 0.06)'};
  }

  [data-theme="light"] &:hover:not(:disabled) {
    background: ${props => props.active ? 'white' : 'rgba(0, 0, 0, 0.03)'};
  }
`;

/* Input Area */
const InputArea = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: ${spacing.lg}px;
  transition: border-color 0.2s;

  &:focus-within {
    border-color: rgba(139, 92, 246, 0.4);
  }

  [data-theme="light"] & {
    background: white;
    border-color: ${colors.lightGray};

    &:focus-within {
      border-color: ${colors.neonViolet};
    }
  }
`;

const TextInput = styled.textarea`
  width: 100%;
  min-height: 160px;
  padding: ${spacing.lg}px;
  background: transparent;
  border: none;
  font-family: ${typography.fontFamily.primary};
  font-size: 15px;
  color: #ffffff;
  resize: vertical;
  box-sizing: border-box;
  line-height: 1.6;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.25);
  }

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};

    &::placeholder {
      color: ${colors.hintGray};
    }
  }
`;

const InputFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing.sm}px ${spacing.lg}px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);

  [data-theme="light"] & {
    border-top-color: ${colors.lightGray};
  }
`;

const CharCount = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);

  [data-theme="light"] & {
    color: ${colors.hintGray};
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid var(--accent-subtle);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

const AnalyzeBtn = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 24px;
  background: ${props => props.disabled
    ? 'rgba(139, 92, 246, 0.15)'
    : 'linear-gradient(135deg, #8b5cf6, #7c3aed)'};
  color: ${props => props.disabled ? 'var(--fg-disabled)' : 'var(--fg-on-accent)'};
  border: none;
  border-radius: 8px;
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(139, 92, 246, 0.35);
  }
`;

/* Error */
const ErrorBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px ${spacing.lg}px;
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 10px;
  color: #f87171;
  font-size: ${typography.fontSize.sm};
  margin-bottom: ${spacing.lg}px;

  [data-theme="light"] & {
    background: #fef2f2;
    border-color: #fecaca;
    color: #dc2626;
  }
`;

const WarningBox = styled.div`
  padding: 12px ${spacing.lg}px;
  background: rgba(234, 179, 8, 0.08);
  border: 1px solid rgba(234, 179, 8, 0.2);
  border-radius: 10px;
  color: #eab308;
  font-size: ${typography.fontSize.sm};
  margin-bottom: ${spacing.lg}px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  [data-theme="light"] & {
    background: #fefce8;
    border-color: #fde68a;
    color: #a16207;
  }
`;

const ErrorIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.2);
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
`;

const SkipBox = styled.div`
  display: flex; flex-direction: column; gap: 10px; padding: 16px ${spacing.lg}px;
  background: rgba(234,179,8,0.08); border: 1px solid rgba(234,179,8,0.25);
  border-radius: 10px; margin-bottom: ${spacing.lg}px;
  [data-theme="light"] & { background: #fffbeb; border-color: #fde68a; }
`;
const SkipText = styled.div`
  color: #eab308; font-size: ${typography.fontSize.sm}; line-height: 1.5;
  white-space: pre-line;
  [data-theme="light"] & { color: #b45309; }
`;
const SkipAction = styled.button`
  align-self: flex-start; padding: 8px 16px; background: rgba(139,92,246,0.12);
  border: 1px solid rgba(139,92,246,0.3); border-radius: 8px; color: ${colors.neonViolet};
  font-size: ${typography.fontSize.sm}; font-weight: ${typography.fontWeight.semibold};
  cursor: pointer; transition: all 0.2s;
  &:hover { background: rgba(139,92,246,0.2); }
  [data-theme="light"] & { background: rgba(139,92,246,0.08); }
`;

/* URL Input */
const UrlInputWrapper = styled.div`
  padding: ${spacing.lg}px;
`;

const UrlInput = styled.input`
  width: 100%;
  padding: 14px ${spacing.lg}px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  font-family: ${typography.fontFamily.primary};
  font-size: 15px;
  color: #ffffff;
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: rgba(139, 92, 246, 0.4);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.25);
  }

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};
    border-color: ${colors.lightGray};

    &:focus { border-color: ${colors.neonViolet}; }
    &::placeholder { color: ${colors.hintGray}; }
  }
`;

/* File Upload */
const FileDropZone = styled.div`
  padding: ${spacing.lg}px;
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DropPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px;
  border: 2px dashed rgba(139, 92, 246, 0.2);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;

  &:hover {
    border-color: rgba(139, 92, 246, 0.4);
    background: rgba(139, 92, 246, 0.03);
  }

  [data-theme="light"] & {
    border-color: ${colors.borderGray};
    &:hover {
      border-color: ${colors.neonViolet};
      background: rgba(139, 92, 246, 0.02);
    }
  }
`;

const DropLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.6);

  [data-theme="light"] & {
    color: ${colors.steelGray};
  }
`;

const DropHint = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);

  [data-theme="light"] & {
    color: ${colors.hintGray};
  }
`;

const FileErrorMsg = styled.div`
  font-size: 13px; color: #ef4444; margin-top: 8px; text-align: center;
`;

const FileSelected = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md}px;
  padding: 14px 20px;
  background: rgba(139, 92, 246, 0.06);
  border: 1px solid rgba(139, 92, 246, 0.15);
  border-radius: 10px;
  width: 100%;
`;

const FileName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};
  }
`;

const FileSize = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  flex-shrink: 0;

  [data-theme="light"] & {
    color: ${colors.hintGray};
  }
`;

const RemoveFileBtn = styled.button`
  padding: 4px 10px;
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 6px;
  background: transparent;
  color: #f87171;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;

  &:hover {
    background: rgba(239, 68, 68, 0.1);
  }

  [data-theme="light"] & {
    border-color: rgba(220, 38, 38, 0.2);
    color: #dc2626;
    &:hover { background: rgba(220, 38, 38, 0.05); }
  }
`;
